import { useEffect, useState } from 'react'
import { Helmet } from 'react-helmet';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useToast, Input, Button } from 'barte-design-system';
import { usePostHog } from '@posthog/react';

import { HeaderPage } from '@/modules/shared/components';
import { ROUTES_PAYFAC, TRACK_EVENTS } from '@payfac/constants'
import { useCreateRatePlan } from '@payfac/services';

import { RatePlanDetails } from '../components';

import { initialData } from './form.constants'

import type { IRatePlanDetails } from "@payfac/types";

import styles from './form.module.css';

export const RatePlanForm = () => {
    const { t } = useTranslation();
    const toast = useToast()
    const navigate = useNavigate();
    const posthog = usePostHog();

    const [data, setData] = useState(initialData)

    const { mutateAsync: createRatePlan, isPending } = useCreateRatePlan()

    const handleNavigateToShow = (id: number) => {
        navigate(ROUTES_PAYFAC.RATE_PLANS.SHOW.fullPath(id));
    };

    const onSubmitLocal = (payload: IRatePlanDetails, onSuccess: () => void) => {
        setData({ ...data, ...payload })

        onSuccess?.()
    }

    const onSubmitData = async () => {
        posthog.capture(TRACK_EVENTS.RATE_PLAN.CREATED_INTENT);

        if (!data.name?.trim()) {
            toast.error({
                title: t('payfac.pageRatePlanFormNameRequired'),
            });

            posthog.capture(TRACK_EVENTS.RATE_PLAN.CREATED_ERROR, {
                error: t('payfac.pageRatePlanFormNameRequired')
            });

            return;
        }

        const res = await createRatePlan({ payload: data });

        if (res.success) {
            posthog.capture(TRACK_EVENTS.RATE_PLAN.CREATED_SUCCESS);

            toast.success({
                title: t('payfac.pageRatePlanFormSuccess'),
            });

            handleNavigateToShow(res?.data?.id)
        } else {
            posthog.capture(TRACK_EVENTS.RATE_PLAN.CREATED_ERROR);

            toast.error({
                title: res?.data?.errors?.[0]?.title,
                description: res?.message,
            });
        }
    }

    useEffect(() => {
        posthog.capture(TRACK_EVENTS.RATE_PLAN.FORM_VIEWED);
    }, []);

    return (
        <div className={styles.container}>
            <Helmet>
                <title>{t('payfac.pageRatePlanFormTitle')} - Barte</title>
            </Helmet>

            <div className={styles['header-actions']}>
                <HeaderPage
                    title={t('payfac.pageRatePlanFormTitle')}
                    withBackButton
                />

                <Button onClick={onSubmitData} loading={isPending}>
                    {t('payfac.pageRatePlanFormSaveButton')}
                </Button>
            </div>

            <div className={styles.content}>
                <Input
                    label={t('payfac.pageRatePlanFormNameLabel')}
                    placeholder={t('payfac.pageRatePlanFormNamePlaceholder')}
                    value={data.name || ''}
                    onChange={(e) => setData({ ...data, name: e.target.value })}
                />

                <RatePlanDetails data={data} isLoading={isPending} onSubmitData={onSubmitLocal} isCreate />
            </div>
        </div>
    );
};
